import { useState, useEffect } from "react";
import axios from "axios";
import { LogOut, Loader2, Megaphone, ShieldCheck } from "lucide-react";
import ProfilePanel from "./ProfilePanel";
import AnnouncementTemplate from "./AnnouncementTemplate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LDC_LOGO = "https://customer-assets.emergentagent.com/job_artist-portal-ldc/artifacts/39dze3hq_Text%20Logo%20Gold_noBG_WEBP.webp";

export default function Dashboard({ token, onLogout, isAdmin = false, onAdmin }) {
  const [profiles, setProfiles] = useState([]);
  const [servicesOptions, setServicesOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAnnounce, setShowAnnounce] = useState(false);

  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadProfiles(); }, []);

  const loadProfiles = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/artist/profile`, { headers: authHeader });
      setProfiles(data.profiles || []);
      setServicesOptions(data.options?.session_services_offered || []);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError("Failed to load profile. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = (panelIndex, updatedProfile) => {
    setProfiles((prev) =>
      prev.map((p, i) => (i === panelIndex ? { ...p, ...updatedProfile } : p))
    );
  };

  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <Loader2 size={40} className="spin gold" />
        <p>LOADING YOUR PROFILE...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen" data-testid="error-screen">
        <p>{error}</p>
        <button className="btn-primary" onClick={loadProfiles}>RETRY</button>
      </div>
    );
  }

  if (showAnnounce) {
    return <AnnouncementTemplate onBack={() => setShowAnnounce(false)} profiles={profiles} servicesOptions={servicesOptions} token={token} />;
  }

  const isMulti = profiles.length > 1;

  return (
    <div className="dashboard" data-testid="dashboard">

      {/* Header */}
      <header className="dash-header">
        <img src={LDC_LOGO} alt="London Drumstick Co." className="dash-logo" />
        <div className="dash-header-right">
          {isAdmin && (
            <button className="btn-announce btn-admin-link" onClick={onAdmin} data-testid="admin-dashboard-btn">
              <ShieldCheck size={15} /> ADMIN
            </button>
          )}
          <button
            className="btn-announce"
            onClick={() => setShowAnnounce(true)}
            data-testid="announce-btn"
          >
            <Megaphone size={15} /> ANNOUNCEMENT
          </button>
          <span className="dash-email" data-testid="user-email">
            {localStorage.getItem("ldc_email")}
          </span>
          <button className="btn-logout" onClick={onLogout} data-testid="logout-btn">
            <LogOut size={16} /> LOGOUT
          </button>
        </div>
      </header>

      {/* Single-profile hero (only when one profile) */}
      {!isMulti && profiles[0] && (
        <SingleHero profile={profiles[0]} />
      )}

      {/* Multi-profile label */}
      {isMulti && (
        <div className="multi-profile-banner">
          <span>YOU HAVE {profiles.length} ARTIST PROFILES ON THIS ACCOUNT</span>
        </div>
      )}

      {/* Panels */}
      <main className={isMulti ? "dash-split" : "dash-content"}>
        {isMulti && <div className="split-divider" />}
        {profiles.map((profile, i) => (
          <ProfilePanel
            key={profile.record_id}
            profile={profile}
            token={token}
            panelIndex={i}
            onUpdate={handleProfileUpdate}
            servicesOptions={servicesOptions}
          />
        ))}
      </main>

      <footer className="dash-footer">
        <p>LONDON DRUMSTICK COMPANY — ARTIST PORTAL</p>
        <div className="dash-footer-right">
          <a href="https://www.londondrumsticks.co.uk" target="_blank" rel="noopener noreferrer">
            VISIT OUR WEBSITE
          </a>
        </div>
      </footer>
    </div>
  );
}

function SingleHero({ profile }) {
  const photo = profile?.photos?.[0];
  const photoUrl = photo?.thumbnails?.large?.url || photo?.url ||
    "https://images.pexels.com/photos/7715823/pexels-photo-7715823.jpeg?auto=compress&cs=tinysrgb&w=400";

  return (
    <section className="profile-hero" data-testid="profile-hero">
      <div className="hero-photo-container">
        <img src={photoUrl} alt={profile?.full_name} className="hero-photo" data-testid="profile-photo" />
        <div className="hero-photo-border" />
      </div>
      <div className="hero-info">
        <h1 className="hero-name" data-testid="hero-name">{profile?.full_name || "YOUR NAME"}</h1>
        <div className="hero-divider" />
        <h2 className="hero-band" data-testid="hero-band">{profile?.band_name || "YOUR BAND"}</h2>
        <p className="hero-label">LONDON DRUMSTICK CO. SPONSORED ARTIST</p>
      </div>
    </section>
  );
}
